import type { Messages } from './en';

/**
 * Fase D (cielo): tiempo, calendario, tifones, terremotos y las llamadas de mamá.
 * Las líneas de práctica de inglés se quedan en inglés a propósito.
 */
export const esSky: Pick<Messages, 'sky'> = {
  sky: {
    mapNames: {},
    zones: {},
    objects: {
      sky_calendar: 'Calendario',
      sky_kit: 'Kit de emergencia',
      sky_umbrellas: 'Paraguas',
    },
    npc: {},
    items: {},

    kinds: {
      sunny: 'Soleado',
      cloudy: 'Nublado',
      rain: 'Lluvia',
      storm: 'Tormenta',
      cool: 'Frente frío',
      typhoon: 'Tifón',
    },
    hud: {
      /** Texto emergente de la caja de la hora. */
      title: (week: number, date: string, kind: string) => `Semana ${week} · ${date} · ${kind}`,
    },

    forecast: {
      title: 'Previsión del tiempo',
      today: 'Hoy',
      tomorrow: 'Mañana',
      rain: (p: number) => `☂ ${p}%`,
      /** Sustituye la línea fija del tiempo del teletipo de noticias por la previsión real de hoy. */
      tickerToday: (kind: string, lo: number, hi: number, p: number) => `Tiempo: ${kind}, ${lo}–${hi}°C, probabilidad de lluvia ${p}% · Recuerda: comprueba la fuente antes de compartir ·`,
      range: (lo: number, hi: number) => `${lo}–${hi}°C`,
      showers: (from: string, to: string) => `Chubascos ${from}–${to}`,
      alert: '🌀 ¡Alerta de tifón! Mañana es día libre por tifón: no hay cole ni trabajo. ¡Quédate en casa!',
      ticker: '🌀 ALERTA DE TIFÓN · Avisos marítimos y terrestres activados · Mañana: día libre por tifón · Guarda agua y carga el móvil ·',
      tips: {
        sunny: 'Calor y sol: bebe agua y ponte una gorra.',
        cloudy: 'Cielo nublado, quizá caiga un chaparrón.',
        rain: 'Día lluvioso: ¡coge el paraguas!',
        storm: 'Tormentas por la tarde: lleva paraguas y aléjate de los árboles altos.',
        cool: 'Ha llegado un frente frío: ponte una chaqueta.',
        typhoon: '¡Tifón! Quédate en casa y lejos de las ventanas.',
      },
    },

    toasts: {
      typhoonEve: '🌀 Alerta de tifón: ¡mañana es día libre por tifón (颱風假)! Prepárate en casa.',
      typhoonDay: '🌀 ¡Día libre por tifón! El cole y las tiendas están cerrados. Quédate a salvo en casa.',
      typhoonPassed: '🌤️ El tifón ha pasado. Las puertas vuelven a estar abiertas.',
      doorClosed: 'Cerrado por el tifón. ¡Quédate a salvo en casa!',
      tooWindy: 'El viento sopla demasiado fuerte. ¡No es seguro salir con un tifón!',
      rainStarts: '🌧️ ¡Empieza a llover!',
      stormStarts: '⛈️ ¡Truenos! Se acerca una tormenta.',
      soaked: '💦 ¡Te has empapado con la lluvia! Compra un paraguas en la tienda.',
      umbrellaOpen: '☂️ Abres el paraguas. ¡Bien sequito!',
      sentHome: '🌀 Día libre por tifón: hoy no hay cole. Te quedas en casa.',
      windy: '🍃 ¡El viento del tifón aúlla ahí fuera!',
      quakeAlert: '📳 ¡Alerta sísmica (地震速報)! Pronto temblará fuerte: ¡protégete la cabeza!',
    },

    calendar: {
      who: 'Calendario',
      today: (date: string, kind: string, temp: number, pct: number) => `Hoy es **${date}**. ${kind}, ${temp}°C, probabilidad de lluvia ${pct}%.`,
      tomorrow: (kind: string, lo: number, hi: number, pct: number) => `Mañana: ${kind}, ${lo}–${hi}°C, lluvia ${pct}%.`,
      tear: 'Arrancas la hoja de ayer. En muchas casas taiwanesas cuelgan calendarios de taco como este.',
      practice: 'Dilo en inglés',
      // Líneas de práctica de inglés.
      sayDate: (date: string) => `Today is ${date}.`,
      say: {
        sunny: 'It’s sunny and hot today.',
        cloudy: 'It’s cloudy today.',
        rain: 'It’s rainy today. I need an umbrella.',
        storm: 'There will be a thunderstorm this afternoon.',
        cool: 'It’s cold today. I need a jacket.',
        typhoon: 'A typhoon is coming. Stay at home!',
      },
    },

    kit: {
      title: '🌀 Lista antitifón',
      intro: 'Prepara la casa antes de que la tormenta empeore. Toca cada tarea cuando la acabes.',
      calm: 'Nuestro kit de emergencia: agua, una linterna, pilas y un botiquín. ¡Listos para tifones y terremotos!',
      calmTomorrow: 'Mañana llega un tifón. Mañana por la mañana, prepara la casa con esta lista.',
      done: '✅ Hecho',
      go: 'Hacerlo',
      goBalcony: 'Ir al balcón',
      all: 'Todo listo. ¡Muy bien, experto en tifones!',
      items: {
        // Líneas de práctica de inglés (los campos `en`).
        water: { icon: '💧', label: 'Llena las botellas de agua y la bañera', en: 'Fill the water bottles.' },
        phone: { icon: '🔋', label: 'Carga el móvil y la batería externa', en: 'Charge the phone.' },
        plants: { icon: '🪴', label: 'Mete dentro las plantas del balcón', en: 'Bring the plants inside.' },
        flashlight: { icon: '🔦', label: 'Busca la linterna y las pilas', en: 'Where is the flashlight?' },
      },
      balconyAsk: '¡El viento aúlla! Las macetas podrían salir volando del balcón. ¿Las metes dentro?',
      balconyYes: '🪴 Meterlas',
      balconyNo: 'Luego',
      balconyDone: 'Las macetas están a salvo dentro.',
    },

    typhoonMorning: {
      who: 'Mamá',
      text: '¡Buenos días! Ya está aquí el tifón, así que hoy es día libre por tifón: no hay cole ni trabajo. ¿Me ayudas a preparar la casa? La lista está en el kit de emergencia, junto a la puerta.',
      good: '«¡Claro, mamá! Lleno el agua y cargo el móvil.»',
      goodMsg: 'Mamá sonríe: «¡Mi pequeño ayudante!»',
      bad: '«¿Un día libre? ¡Bien, me voy a jugar fuera!»',
      badMsg: 'Mamá frunce el ceño: «¡Ni hablar! Ahí fuera es peligroso.»',
    },

    quake: {
      who: '¡Terremoto!',
      text: '¡El suelo tiembla! Las cosas traquetean y se caen. ¿Qué deberías hacer?',
      drop: '🫣 Agáchate, cúbrete la cabeza y agárrate',
      outside: '🏃 Salir corriendo a la calle lo más rápido posible',
      lift: '🛗 Bajar en ascensor',
      window: '🪟 Ponerte junto a la ventana a mirar',
      right: '¡Correcto! Agáchate, métete debajo de una mesa, cúbrete la cabeza y agárrate hasta que pare de temblar.',
      wrongOutside: '¡No es seguro! Si corres mientras tiembla, te puedes caer o te pueden golpear cristales y carteles.',
      wrongLift: 'Nunca cojas el ascensor durante un terremoto: se puede parar y dejarte atrapado. Usa las escaleras cuando pare de temblar.',
      wrongWindow: '¡Las ventanas se pueden romper! Aléjate de los cristales.',
      // Línea de práctica de inglés.
      phrase: 'Drop, cover and hold on!',
      lesson: 'Dilo en inglés: **“Drop, cover and hold on!”** (¡Agáchate, cúbrete y agárrate!). En Taiwán hay muchos terremotos, así que todo el mundo lo practica en el cole.',
      after: 'Ha dejado de temblar. Uf… todos están bien.',
      momWho: 'Mamá',
      momHome: 'Mamá viene corriendo: «¿Estás bien? ¿Se te ha caído algo encima?»',
      momGood: '«¡Estoy bien, mamá! Me agaché, me cubrí y me agarré.»',
      momGoodMsg: 'Mamá te abraza fuerte.',
      momBad: '«No ha sido nada, deja de preocuparte.»',
      momBadMsg: 'Mamá suspira, todavía temblando.',
    },

    umbrella: {
      useRain: '☂️ Abres el paraguas. La lluvia repiquetea encima.',
      useDry: 'Ahora no llueve. Guardas el paraguas en la mochila.',
      who: 'Paragüero',
      ask: (price: number) => `Un cubo de paraguas junto a la puerta, como en todas las tiendas de conveniencia de Taiwán. Uno cuesta ${price} monedas.`,
      buy: (price: number) => `☂️ Comprar uno (${price}🪙)`,
      no: 'No, gracias',
      poor: 'No tienes suficientes monedas…',
      bought: '☂️ ¡Ya tienes paraguas! La lluvia ya no te detiene.',
      have: 'Ya llevas un paraguas en la mochila.',
    },

    calls: {
      momTyphoon: {
        caller: 'Mamá ❤️',
        desc: 'Preocupada por el tifón',
        intro: 'Cariño, ¡el tifón se está acercando! ¿Dónde estás? Vuelve a casa antes de que sople fuerte el viento, y no te acerques al río.',
        good: '«Ya vuelvo a casa, mamá. ¡Te ayudo a prepararlo todo!»',
        goodMsg: 'Mamá suena aliviada: «Gracias, cielo. ¡Ten cuidado!»',
        bad: '«Solo es un poco de viento. Me quedo fuera.»',
        badMsg: 'A mamá se le encoge la voz de preocupación…',
      },
      momQuake: {
        caller: 'Mamá ❤️',
        desc: 'Llamando justo después del terremoto',
        intro: '¿Has notado el terremoto? ¿Te has hecho daño? ¿Dónde estás ahora mismo?',
        good: '«¡Estoy bien, mamá! Me agaché, me cubrí y me agarré.»',
        goodMsg: 'Mamá vuelve a respirar: «¡Muy bien! Eso es justo lo que hay que hacer.»',
        bad: '«Jo, mamá, no ha sido nada.»',
        badMsg: 'Mamá dice en voz baja: «Es que me he asustado por ti…»',
      },
      momRain: {
        caller: 'Mamá ❤️',
        desc: 'Está lloviendo',
        intro: '¡Está lloviendo! ¿Has cogido paraguas? No te mojes, que te vas a resfriar.',
        good: '«¡Gracias, mamá! Compro un paraguas en la tienda.»',
        goodMsg: 'Mamá: «Buen chico. ¡Sécate el pelo cuando llegues a casa!»',
        bad: '«La lluvia me da igual.»',
        badMsg: 'Mamá suspira: «Pues luego no vuelvas a casa estornudando…»',
      },
    },

    journal: {
      typhoonReady: 'Preparé la casa para un tifón',
      quakeSafe: 'Me protegí en un terremoto: ¡agáchate, cúbrete y agárrate!',
    },
  },
};
